import { useEffect, useRef, useState } from "react";
import { OverlayView, Circle, InfoWindow } from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { convertMetersToFeet, parseDateFlexible } from "../../utils/utils";
import { useOfficerLocation } from "./OfficerLocationContext";

export function createOfficerMarker(
  officer,
  map = null,
  icon = null,
  fillColor = "#1976D2",
  labelColor = "#000000",
  onClick = null
) {
  const labelText = `${officer?.deptAttribute?.ori?.abbreviation}-${officer?.deptAttribute?.badge}`;
  const marker = new window.google.maps.Marker({
    position: {
      lat: parseFloat(officer.lat),
      lng: parseFloat(officer.lng),
    },
    map,
    icon: icon || {
      url:
        "data:image/svg+xml;charset=UTF-8," +
        encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 48 60" fill="none">
          <path fill="${fillColor}" d="M24 0C21.24 2.63 16.82 4 12 4S2.76 2.63 0 0c0 15.58 2.11 26.71 6.37 33.36C10.63 40.01 17.22 44.64 24 48c6.78-3.36 13.37-7.99 17.63-14.64C45.89 26.71 48 15.58 48 0c-2.76 2.63-7.18 4-12 4s-9.24-1.37-12-4zm0 15l2.9 5.88 6.49.94-4.7 4.58 1.11 6.47L24 29.75l-5.8 3.05 1.11-6.47-4.7-4.58 6.49-.94L24 15z"/>
         </svg>
      `),
      scaledSize: new window.google.maps.Size(30, 30),
      anchor: new window.google.maps.Point(15, 15),
      labelOrigin: new window.google.maps.Point(15, -8),
    },
    label: {
      text: labelText,
      fontSize: "14px",
      fontWeight: "bold",
      color: labelColor,
    },
  });

  marker._officerLabel = labelText;
  marker._officerId = officer.id;

  if (onClick) {
    marker.addListener("click", () => onClick(officer, marker));
  }

  return marker;
}

const OfficerClusterer = ({
  map,
  showOfficerAccuracy,
  findOfficer = null,
  setFindOfficer = () => {},
}) => {
  const [clusterData, setClusterData] = useState([]);
  const [closedClusters, setClosedClusters] = useState([]);
  const clusterAccumulator = useRef([]);
  const markersRef = useRef([]);
  const clustererRef = useRef(null);
  const rafIdRef = useRef(null);
  const [activeOfficer, setActiveOfficer] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const activeOfficerRef = useRef(null);
  const [mapZoom, setMapZoom] = useState(null);
  const { officersRef } = useOfficerLocation();
  const officers = officersRef.current;
  // Track map zoom and bounds changes
  useEffect(() => {
    if (!map) return;
    const zoomListener = map.addListener("zoom_changed", () => {
      setMapZoom(map.getZoom());
    });
    const idleListener = map.addListener("idle", () => {
      setMapZoom(map.getZoom());
    });
    return () => {
      window.google.maps.event.removeListener(zoomListener);
      window.google.maps.event.removeListener(idleListener);
    };
  }, [map]);

  const OFFICER_CLUSTER_ICON = count => ({
    url:
      "data:image/svg+xml;charset=UTF-8," +
      encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="48" viewBox="0 0 48 60">
          <path fill="#1976D2" d="M24 0C21.24 2.63 16.82 4 12 4S2.76 2.63 0 0c0 15.58 2.11 26.71 6.37 33.36C10.63 40.01 17.22 44.64 24 48c6.78-3.36 13.37-7.99 17.63-14.64C45.89 26.71 48 15.58 48 0c-2.76 2.63-7.18 4-12 4s-9.24-1.37-12-4z"/>
          <text x="24" y="33" text-anchor="middle" font-size="18" fill="white" font-weight="bold">${count}</text>
        </svg>
      `),
    scaledSize: new window.google.maps.Size(40, 40),
  });

  useEffect(() => {
    if (!map || !window.google || !officers?.length) return;

    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    const markers = officers.map(officer => {
      const fillColor =
        findOfficer?.toString() === officer?.id?.toString()
          ? "#800080"
          : "#1976D2";
      const labelColor =
        findOfficer?.toString() === officer?.id?.toString()
          ? "#ff0000"
          : "#000000";

      return createOfficerMarker(
        officer,
        map,
        null,
        fillColor,
        labelColor,
        (officerObj, marker) => {
          setActiveOfficer(officerObj);
          setActiveMarker(marker);
          activeOfficerRef.current = officerObj;
        }
      );
    });

    markersRef.current = markers;
    clusterAccumulator.current = [];

    return () => {
      markers.forEach(m => m.setMap(null));
      cancelAnimationFrame(rafIdRef.current);
      setClusterData([]);
    };
  }, [map, officers, closedClusters, findOfficer]);

  useEffect(() => {
    if (
      !map ||
      !window.google ||
      !officers?.length ||
      !markersRef.current.length
    )
      return;

    if (clustererRef.current) {
      clustererRef.current?.clearMarkers();
    }
    const clusterer = new MarkerClusterer({
      map,
      markers: markersRef.current,
      renderer: {
        render: ({ count, markers, position }) => {
          const labels = markers.map(m => m._officerLabel);
          const cluster = {
            id: labels.sort().join(","),
            position,
            labels,
          };

          clusterAccumulator.current.push(cluster);

          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = requestAnimationFrame(() => {
            if (clusterAccumulator.current.length > 0) {
              setClusterData(
                clusterAccumulator.current.filter(
                  c => !closedClusters.includes(c.id)
                )
              );
            }
            clusterAccumulator.current = [];
          });

          const clusterMarker = new window.google.maps.Marker({
            position,
            icon: OFFICER_CLUSTER_ICON(count),
          });

          clusterMarker.addListener("mouseover", () => {
            if (closedClusters.includes(cluster.id)) {
              setClosedClusters(prev => prev.filter(id => id !== cluster.id));
            }
          });

          return clusterMarker;
        },
      },
    });

    clustererRef.current = clusterer;

    return () => {
      clustererRef.current?.clearMarkers();
      cancelAnimationFrame(rafIdRef.current);
      setClusterData([]);
    };
  }, [map, officers, closedClusters, mapZoom]);

  useEffect(() => {
    if (!map || !clustererRef.current) return;

    const checkForDecluster = () => {
      rafIdRef.current = requestAnimationFrame(() => {
        const clusterer = clustererRef.current;
        const numMarkers = clusterer?.markers?.length;
        const numClusters = clusterer?.clusters?.length;

        if (!!numMarkers && !!numClusters && numMarkers === numClusters) {
          setClusterData([]);
        }
      });
    };

    const listener = map.addListener("idle", checkForDecluster);

    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      window.google.maps.event.removeListener(listener);
    };
  }, [map]);

  const unclusteredOfficerLabels = clusterData.flatMap(c => c.labels);

  useEffect(() => {
    if (findOfficer) {
      const timeout = setTimeout(() => {
        setFindOfficer(null);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [findOfficer, setFindOfficer, map, officers]);

  return (
    <>
      {showOfficerAccuracy &&
        officers
          .filter(officer => {
            const label = `${officer?.deptAttribute?.ori?.abbreviation}-${officer?.deptAttribute?.badge}`;
            return !unclusteredOfficerLabels.includes(label);
          })
          .map(officer => (
            <Circle
              key={`circle-${officer.id}`}
              center={{
                lat: parseFloat(officer.lat),
                lng: parseFloat(officer.lng),
              }}
              radius={officer?.accuracy}
              options={{
                strokeColor: "#1976D2",
                strokeOpacity: 0.9,
                strokeWeight: 2,
                fillColor: "#1976D2",
                fillOpacity: 0.2,
              }}
            />
          ))}
      {activeOfficer && (
        <InfoWindow
          key={`info-${activeOfficer.id}`}
          position={{
            lat:
              parseFloat(activeMarker?.position?.lat()) ||
              parseFloat(activeOfficer?.lat),
            lng:
              parseFloat(activeMarker?.position?.lng()) ||
              parseFloat(activeOfficer?.lng),
          }}
          onCloseClick={() => {
            setActiveOfficer(null);
            setActiveMarker(null);
            activeOfficerRef.current = null;
          }}
        >
          <div>
            <strong>
              <p style={{ margin: "5px 0" }}>
                {activeOfficer.deptAttribute.ori.abbreviation}-
                {activeOfficer.deptAttribute.badge}
              </p>
            </strong>
            <p style={{ margin: "5px 0" }}>
              Last Updated:{" "}
              {parseDateFlexible(activeOfficer.locationUpdatedAt)?.format(
                "MM/DD/YYYY HH:mm"
              ) || ""}
            </p>
            <p style={{ margin: "5px 0" }}>
              Accuracy:{" "}
              {activeOfficer?.accuracy !== undefined
                ? convertMetersToFeet(Number(activeOfficer.accuracy)) + " ft"
                : ""}
            </p>
            <p style={{ margin: "5px 0" }}>
              Source: {activeOfficer?.locationSource}
            </p>
          </div>
        </InfoWindow>
      )}
      {clusterData
        .filter(cluster => !closedClusters.includes(cluster.id))
        .map(cluster => (
          <OverlayView
            key={cluster.id}
            position={cluster.position}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div
              style={{
                background: "white",
                border: "1px solid #888",
                padding: "6px 8px",
                borderRadius: 6,
                fontSize: 12,
                marginTop: -50,
                marginLeft: 20,
                display: "inline-block",
                whiteSpace: "nowrap",
                width: "fit-content",
                maxWidth: "90vw",
                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                position: "relative",
              }}
            >
              <button
                style={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 14,
                }}
                aria-label="Close overlay"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setClosedClusters(prev => [...prev, cluster.id]);
                }}
              >
                ×
              </button>
              <strong>Officers:</strong>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {cluster.labels.map((l, i) => (
                  <li key={i}>{l}</li>
                ))}
              </ul>
            </div>
          </OverlayView>
        ))}
    </>
  );
};

export default OfficerClusterer;
